import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getDashboardStats, getStock, getDailyUsageReport, seedDatabase } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Package, 
  Boxes, 
  DollarSign, 
  AlertTriangle, 
  Activity,
  ArrowRight,
  Plus,
  Wrench,
  ArrowRightLeft,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const TOOLTIP_STYLE = { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', boxShadow: '0 4px 12px -2px rgba(0,0,0,0.1)' };
const DOT_STYLE = { fill: '#0776BD', strokeWidth: 2 };
const ACTIVE_DOT_STYLE = { fill: '#F0C500', stroke: '#0776BD', strokeWidth: 2, r: 6 };

const StatCard = ({ title, value, icon: Icon, trend, highlight = false }) => (
  <Card className={`bg-white rounded-xl border ${highlight ? 'border-[#F0C500]' : 'border-gray-200'} shadow-soft hover:shadow-medium transition-all duration-200`} data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-[#6B7280] uppercase tracking-wide mb-1">{title}</p>
          <p className="text-3xl font-bold font-heading text-[#1F2937]">{value}</p>
          {trend && (
            <p className="text-sm text-[#6B7280] mt-1">{trend}</p>
          )}
        </div>
        <div className={`p-3 rounded-xl ${highlight ? 'bg-[#F0C500]/10' : 'bg-[#E6F2FA]'}`}>
          <Icon size={24} className={highlight ? 'text-[#F0C500]' : 'text-[#0776BD]'} strokeWidth={1.5} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const QuickAction = ({ to, icon: Icon, label }) => (
  <Link to={to} data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <Button 
      variant="outline" 
      className="h-auto py-4 px-6 flex flex-col items-center gap-2 border-2 border-gray-200 hover:border-[#0776BD] hover:bg-[#E6F2FA] transition-all w-full rounded-xl"
    >
      <div className="p-2 bg-[#E6F2FA] rounded-lg">
        <Icon size={24} strokeWidth={1.5} className="text-[#0776BD]" />
      </div>
      <span className="text-sm font-medium text-[#1F2937]">{label}</span>
    </Button>
  </Link>
);

const LowStockItem = ({ item }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
    <div className="flex-1 min-w-0">
      <p className="font-medium text-[#1F2937] truncate">{item.item_name}</p>
      <p className="text-sm text-[#6B7280] font-mono">{item.internal_code}</p>
    </div>
    <div className="flex items-center gap-3">
      <div className="text-right">
        <p className="font-bold text-lg text-[#1F2937]">{item.quantity}</p>
        <p className="text-xs text-[#6B7280]">/ {item.minimum_quantity} min</p>
      </div>
      <Badge className="bg-[#F0C500]/20 text-[#1F2937] border-0 font-medium">Low</Badge>
    </div>
  </div>
);

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [usageData, setUsageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, stockRes, usageRes] = await Promise.all([
        getDashboardStats(),
        getStock({ low_stock_only: true }),
        getDailyUsageReport({ days: 7 })
      ]);
      
      setStats(statsRes.data);
      setLowStockItems(stockRes.data.slice(0, 5));
      setUsageData(usageRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      await seedDatabase();
      await fetchData();
    } catch (error) {
      toast.error('Failed to seed data');
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="heading-2 text-[#1F2937]">Welcome back, {user?.name?.split(' ')[0]}</h1>
          <p className="text-[#6B7280]">Here's what's happening with your inventory today.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="gap-2 rounded-lg border-gray-300 text-[#6B7280] hover:text-[#1F2937] hover:bg-gray-50" data-testid="refresh-btn">
            <RefreshCw size={18} />
            Refresh
          </Button>
          {(user?.role === 'admin' || user?.role === 'manager') && stats?.total_items === 0 && (
            <Button onClick={handleSeedData} disabled={seeding} className="gap-2 bg-[#0776BD] hover:bg-[#055A92] rounded-lg" data-testid="seed-btn">
              {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus size={18} />}
              Load Sample Data
            </Button>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Total Items" 
          value={stats?.total_items || 0}
          icon={Package}
        />
        <StatCard 
          title="Inventories" 
          value={stats?.total_inventories || 0}
          icon={Boxes}
        />
        <StatCard 
          title="Stock Value" 
          value={`$${(stats?.total_stock_value || 0).toFixed(2)}`}
          icon={DollarSign}
        />
        <StatCard 
          title="Low Stock Alerts" 
          value={stats?.low_stock_count || 0}
          icon={AlertTriangle}
          highlight={stats?.low_stock_count > 0}
        />
      </div>

      {/* Quick Actions */}
      {(user?.role === 'admin' || user?.role === 'manager' || user?.role === 'technician') && (
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-[#1F2937]">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <QuickAction to="/usage" icon={Wrench} label="Record Usage" />
              {(user?.role === 'admin' || user?.role === 'manager') && (
                <>
                  <QuickAction to="/restock" icon={Plus} label="Restock" />
                  <QuickAction to="/transfer" icon={ArrowRightLeft} label="Transfer" />
                  <QuickAction to="/items" icon={Package} label="Manage Items" />
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts and Low Stock */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage Chart */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg text-[#1F2937]">Usage Trend (7 Days)</CardTitle>
            <Activity size={20} className="text-[#6B7280]" />
          </CardHeader>
          <CardContent>
            {usageData.length > 0 ? (
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={usageData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { weekday: 'short' })}
                      stroke="#6B7280"
                      fontSize={12}
                    />
                    <YAxis stroke="#6B7280" fontSize={12} />
                    <Tooltip 
                      contentStyle={TOOLTIP_STYLE}
                      formatter={(value, name) => [
                        name === 'usage_count' ? `${value} items` : `$${value.toFixed(2)}`,
                        name === 'usage_count' ? 'Items Used' : 'Value'
                      ]}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="usage_count" 
                      stroke="#0776BD" 
                      strokeWidth={2}
                      dot={DOT_STYLE}
                      activeDot={ACTIVE_DOT_STYLE}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-[#6B7280]">
                No usage data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Low Stock Alerts */}
        <Card className={`bg-white rounded-xl border ${lowStockItems.length > 0 ? 'border-[#F0C500]' : 'border-gray-200'} shadow-soft`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
              <AlertTriangle size={20} className="text-[#F0C500]" />
              Low Stock Alerts
            </CardTitle>
            <Link to="/inventory?low_stock=true" data-testid="view-all-low-stock">
              <Button variant="ghost" size="sm" className="gap-1 text-[#0776BD] hover:text-[#055A92] hover:bg-[#E6F2FA]">
                View All <ArrowRight size={16} />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {lowStockItems.length > 0 ? (
              <div>
                {lowStockItems.map((item) => (
                  <LowStockItem key={item.id} item={item} />
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-[#6B7280]">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <AlertTriangle size={24} className="text-green-600" />
                </div>
                <p className="font-medium">All items are well stocked</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Today's Activity */}
      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-[#1F2937]">Today's Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="p-4 bg-[#E6F2FA] rounded-xl">
              <Activity size={32} className="text-[#0776BD]" />
            </div>
            <div>
              <p className="text-3xl font-bold font-heading text-[#1F2937]">{stats?.today_transactions || 0}</p>
              <p className="text-[#6B7280]">Transactions today</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
